import classNames from 'classnames';
import React, { ForwardedRef } from 'react';
import { FormattedMessage } from 'react-intl';

import PillButton from '../buttons/PillButton';
import Summary from '../summaries/Summary';
import DocumentsRowBlock, { DocumentsRowBlockProps } from './DocumentsRowBlock';

import styles from '../../styles/blocks/summary-row-block.module.css';

export interface SummaryRowBlockProps extends DocumentsRowBlockProps {
    url?: string | null;
    summary?: Record<string, unknown> | null;
    summaryComponent?: React.ElementType;
    summaryPosition?: null | 'center' | 'inline' | 'bottom';
    summaryImagePosition?: null | 'inline' | 'left' | 'right';
    background?: Background | string | null;
    presentation?: string | null;
    loading?: null | 'lazy';
    withBorderRadius?: boolean;
    withoutSummaryImage?: boolean;
    withoutSummarySponsor?: boolean;
    className?: string | null;
    innerClassName?: string | null;
    summaryClassName?: string | null;
    summaryTitleClassName?: string | null;
    summaryDescriptionClassName?: string | null;
    buttonClassName?: string | null;
}

function SummaryRowBlock({
    url = null,
    summary = null,
    summaryComponent: SummaryComponent = Summary,
    summaryPosition = null,
    summaryImagePosition = null,
    background = null,
    presentation = null,
    loading = 'lazy',
    withBorderRadius = false,
    withoutSummaryImage = false,
    withoutSummarySponsor = false,
    className = null,
    innerClassName = null,
    summaryClassName = null,
    summaryTitleClassName = null,
    summaryDescriptionClassName = null,
    buttonClassName = null,
    ref,
    ...props
}: SummaryRowBlockProps & { ref?: ForwardedRef<HTMLDivElement> | null }) {
    const { url: summaryUrl = null, image = null } = summary || {};
    const finalUrl = url || summaryUrl;
    const summaryElement = (
        <SummaryComponent
            withLink
            link={finalUrl}
            {...summary}
            loading={loading}
            withoutImage={withoutSummaryImage}
            withoutSponsor={withoutSummarySponsor}
            placeholder={summary === null}
            className={classNames([
                styles.summary,
                {
                    [styles.withImage]: !withoutSummaryImage && image !== null,
                },
                summaryClassName,
            ])}
            innerClassName={styles.summaryInner}
            titleClassName={classNames([styles.summaryTitle, summaryTitleClassName])}
            descriptionClassName={classNames([
                styles.summaryDescription,
                summaryDescriptionClassName,
            ])}
            imageClassName={styles.summaryImage}
            imagePosition={summaryImagePosition || (summaryPosition === 'inline' ? null : 'left')}
        />
    );
    return (
        <DocumentsRowBlock
            ref={ref}
            loading={loading}
            withBorderRadius={withBorderRadius}
            className={classNames([
                styles.container,
                {
                    [styles.rowList]: presentation === 'row',
                    [styles.responsiveList]: presentation === null,
                    [styles[summaryPosition]]: summaryPosition !== null,
                    [styles.withBackground]: background !== null,
                    [styles.withBorderRadius]: withBorderRadius,
                    [className]: className !== null,
                },
            ])}
            innerClassName={classNames([styles.inner, innerClassName])}
            listClassName={styles.list}
            backgroundClassName={styles.background}
            itemsClassName={styles.items}
            header={
                <div className={styles.header}>
                    {summaryPosition !== 'bottom' ? summaryElement : null}
                    {finalUrl !== null && summaryPosition !== 'inline' ? (
                        <PillButton
                            compact
                            bordered
                            href={`${finalUrl}?_ref=view_all`}
                            className={classNames([styles.button, buttonClassName])}
                        >
                            <FormattedMessage
                                defaultMessage="Voir tout"
                                description="Button label"
                            />
                        </PillButton>
                    ) : null}
                </div>
            }
            footer={summaryPosition === 'bottom' ? summaryElement : null}
            background={background}
            presentation={presentation || 'responsive'}
            {...props}
        />
    );
}

export default SummaryRowBlock;
